import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion, parseProductResponseBody } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

let activeQuickAddRequestId = 0;
/** @type {Promise<void> | null} */
let activeQuickAddClosePromise = null;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Element | null>>} */
  #pendingProductPageRequests = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;

    // 商品卡片切换变体后仍保持预览入口，不回退到直接加购。
    this.setAttribute('data-quick-add-button', 'choose');
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#cachedContent.clear();
    this.#pendingProductPageRequests.clear();
  };

  #handleBreakpointChange = () => {
    void this.#closeQuickAddModal();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const currentUrl = this.productPageUrl;
    const requestId = ++activeQuickAddRequestId;
    const dialogComponent = document.getElementById('quick-add-dialog');
    const modalContent = document.getElementById('quick-add-modal-content');

    const pendingClose = activeQuickAddClosePromise;
    if (pendingClose) {
      await pendingClose;
      if (requestId !== activeQuickAddRequestId) return;
    }

    dialogComponent?.setAttribute('loading', '');
    modalContent?.setAttribute('aria-busy', 'true');
    this.#openQuickAddModal();

    try {
      // Check if we have cached content for this URL
      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        productGrid = (await this.fetchProductPage(currentUrl)) || undefined;
      }

      if (requestId !== activeQuickAddRequestId) return;
      if (!productGrid) {
        this.#clearQuickAddModalContent();
        await this.#closeQuickAddModal(requestId);
        return;
      }

      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      if (requestId !== activeQuickAddRequestId) return;
      this.#updateVariantPicker(productGrid);
    } catch (error) {
      if (requestId !== activeQuickAddRequestId) return;
      this.#clearQuickAddModalContent();
      await this.#closeQuickAddModal(requestId);
      console.error('Quick add 商品信息加载失败', error);
    } finally {
      if (requestId === activeQuickAddRequestId) {
        dialogComponent?.removeAttribute('loading');
        modalContent?.removeAttribute('aria-busy');
      }
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #clearQuickAddModalContent() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    // 请求失败时先移除旧商品，避免关闭动画或下一帧打开期间出现可操作的错误内容。
    modalContent.replaceChildren();
    modalContent.removeAttribute('view-event-payload');
  }

  /** @param {number | undefined} requestId */
  #closeQuickAddModal = async (requestId) => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    // showDialog 在下一帧才真正打开；先跨过该帧，确保失败关闭不会被延迟打开覆盖。
    await new Promise((resolve) => requestAnimationFrame(resolve));
    if (requestId !== undefined && requestId !== activeQuickAddRequestId) return;

    const closePromise = dialogComponent.closeDialog();
    activeQuickAddClosePromise = closePromise;

    try {
      await closePromise;
    } finally {
      if (activeQuickAddClosePromise === closePromise) {
        activeQuickAddClosePromise = null;
      }
    }
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Element | null>}
   */
  fetchProductPage(productPageUrl) {
    if (!productPageUrl) return Promise.resolve(null);

    const cachedContent = this.#cachedContent.get(productPageUrl);
    if (cachedContent) return Promise.resolve(cachedContent);

    const pendingRequest = this.#pendingProductPageRequests.get(productPageUrl);
    if (pendingRequest) return pendingRequest;

    const request = this.#requestProductPage(productPageUrl).finally(() => {
      if (this.#pendingProductPageRequests.get(productPageUrl) === request) {
        this.#pendingProductPageRequests.delete(productPageUrl);
      }
    });
    this.#pendingProductPageRequests.set(productPageUrl, request);

    return request;
  }

  /**
   * 执行一次商品页请求并缓存可复用的商品网格。
   * @param {string} productPageUrl
   * @returns {Promise<Element | null>}
   */
  async #requestProductPage(productPageUrl) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    try {
      const response = await fetch(productPageUrl, {
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = parseProductResponseBody(responseText);
      const gridElement = html.querySelector('[data-product-grid-content]');
      if (!gridElement) return null;
      if (abortController.signal.aborted) return null;

      const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
      this.#cachedContent.set(productPageUrl, productGrid);

      return productGrid;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      if (this.#abortController === abortController) {
        this.#abortController = null;
      }
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // 弹窗只复用商品核心信息，排除商品页主图下方的长详情。
    productGrid.querySelector('.fanka-product-detail-description')?.remove();

    // 同步 view-event-payload 并将商品页内容 morph 到弹窗的 product-component。
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // 等待变体更新数据。
    event.promise
      .then(({ detail }) => {
        this.#updatePreviewImage(detail?.resource?.featured_media?.preview_image, detail?.resource?.featured_media?.alt);

        const anchorElement = /** @type {HTMLAnchorElement | null} */ (
          detail?.html?.querySelector('.view-product-title a')
        );
        const viewProductLink = /** @type {HTMLAnchorElement | null} */ (this.querySelector('.view-product-title a'));
        if (anchorElement && viewProductLink) viewProductLink.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /**
   * 将当前弹窗中唯一可见的主图同步为已选变体的主图。
   * @param {{ src?: string, srcset?: string, width?: number, height?: number, alt?: string } | undefined} previewImage
   * @param {string | null | undefined} mediaAlt
   */
  #updatePreviewImage(previewImage, mediaAlt) {
    if (!previewImage?.src) return;

    const previewImages = this.querySelectorAll(
      '.product-information__media :is(slideshow-slide:first-child, .media-gallery__grid .product-media-container:first-child) .product-media__image'
    );

    for (const image of previewImages) {
      if (!(image instanceof HTMLImageElement)) continue;

      image.src = previewImage.src;
      image.srcset = previewImage.srcset || previewImage.src;

      if (Number.isFinite(previewImage.width) && previewImage.width > 0) {
        image.width = previewImage.width;
      }
      if (Number.isFinite(previewImage.height) && previewImage.height > 0) {
        image.height = previewImage.height;
      }

      const alt = previewImage.alt || mediaAlt;
      if (typeof alt === 'string') image.alt = alt;
    }
  }

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
